import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Clock, MoreVertical, Star } from 'lucide-react';
import {
  MenuPositionType,
  TaskDeleteHandler,
  TaskItem,
  TaskSection,
  TaskUpdateHandler,
} from './types/dashboardTypes';
import { formatDateInfo, getTaskContext } from './utils/dashboardUtils';
import { calculateOptimalPopupPosition } from '../../lib/utils';
import DashboardTaskMenu from './DashboardTaskMenu';
import { FloatingDatePicker, UserInfoTooltip } from '../shared';
import { logger } from '../../utils/logger';
import { getDashboardSectionColor } from '../../utils/colorUtils';
import { useViewport } from '../../contexts/ViewportContext';

interface DashboardTaskCardProps {
  task: TaskItem;
  sectionType: TaskSection;
  projects: Array<{ id: string; name: string }>;
  onEditTask: (task: TaskItem) => void;
  onUpdateTask: TaskUpdateHandler;
  onDeleteTask: TaskDeleteHandler;
  onNavigateToTask: (task: TaskItem) => void;
  customDragHandlers?: Record<string, any>; // For drag and drop functionality
}

const DashboardTaskCard: React.FC<DashboardTaskCardProps> = ({
  task,
  sectionType,
  projects,
  onEditTask,
  onUpdateTask,
  onDeleteTask,
  onNavigateToTask,
  customDragHandlers,
}) => {
  const { isMobile } = useViewport();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [menuButtonPosition, setMenuButtonPosition] = useState<MenuPositionType | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { projectName, topicName } = getTaskContext(task, projects);
  const dateInfo = task.deadline ? formatDateInfo(task.deadline) : null;

  // Get colors from utilities
  const borderColor = getDashboardSectionColor(sectionType, 'border');
  const bgColor = getDashboardSectionColor(sectionType, 'bg');

  // Log drag handlers if they exist
  useCallback(() => {
    if (customDragHandlers) {
      logger.component('DashboardTaskCard has drag handlers', {
        taskId: task.id,
        sectionType,
        handlers: Object.keys(customDragHandlers),
      });
    }
  }, [customDragHandlers, task.id, sectionType]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent task edit modal from opening
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();

    logger.component('Menu button clicked', { taskId: task.id, isOpen: isMenuOpen });

    // Toggle menu state
    setIsMenuOpen(!isMenuOpen);

    // Calculate position for the menu
    if (!isMenuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const menuWidth = 190; // Width of menu + some padding
      const menuHeight = 200; // Approximate height of menu

      const position = calculateOptimalPopupPosition(
        { top: rect.bottom, left: rect.left },
        { width: menuWidth, height: menuHeight },
        { x: 0, y: 5 }
      );

      setMenuButtonPosition({
        top: position.top,
        left: position.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const handleOpenDatePicker = (e: React.MouseEvent) => {
    logger.component('DashboardTaskCard.handleOpenDatePicker called', {
      taskId: task.id,
      isMobile,
      hasCurrentTarget: e.currentTarget ? true : false,
    });

    if (isMobile) {
      // For mobile, we just need to open the date picker
      // We don't need position information since we'll use fullscreen
      setIsDatePickerOpen(true);
      return;
    }

    // Only try to get the button position on desktop or if we have a currentTarget
    if (!isMobile && e.currentTarget) {
      // Get button position for the date picker
      const rect = e.currentTarget.getBoundingClientRect();

      // Use the shared calculateOptimalPopupPosition function for date picker too
      const datePickerWidth = 280; // Approximate width of date picker
      const datePickerHeight = 350; // Approximate height of date picker

      const position = calculateOptimalPopupPosition(
        { top: rect.bottom, left: rect.left },
        { width: datePickerWidth, height: datePickerHeight },
        { x: 0, y: 5 }
      );

      setMenuButtonPosition({
        top: position.top,
        left: position.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      // Fallback position for the date picker in case we don't have a current target
      setMenuButtonPosition({
        top: window.innerHeight / 4,
        left: window.innerWidth / 2 - 140, // Half the date picker width
        width: 50,
        height: 30,
      });
    }

    // Open the date picker
    setIsDatePickerOpen(true);
  };

  return (
    <div
      key={task.id}
      className={`p-3 pt-2 bg-white border-l-[5px] ${borderColor} rounded-lg mb-3 cursor-pointer hover:shadow-md transition-shadow shadow relative overflow-hidden group ${(task as any).isHighlighted && sectionType !== 'highlighted' ? 'ring-1 ring-yellow-300' : ''}`}
      onClick={() => onEditTask(task)}
      {...customDragHandlers} // Add drag handlers from parent component
      // Add a backup dragstart event in case the custom handlers don't work
      onDragStart={e => {
        console.log('BACKUP DRAG START', task.id);

        // If we have custom handlers with onDragStart, don't override
        if (customDragHandlers?.onDragStart) return;

        // Otherwise use our own handler
        e.dataTransfer.setData(
          'text/plain',
          JSON.stringify({
            id: task.id,
            title: task.title,
            section: sectionType,
          })
        );
      }}
    >
      {/* Menu buttons - always visible on mobile, hover visible on desktop */}
      <div className="absolute right-2 top-2 task-menu-container z-10 flex items-center">
        {/* Mark as done button with hover animation */}
        {!isMobile && (
          // Desktop version - with hover animation
          <button
            className={cn(
              'group/mark-done rounded-full transition-all py-0.5 px-1.5 flex items-center overflow-hidden',
              'opacity-0 group-hover:opacity-100 w-8 hover:w-[72px] hover:bg-green-100 hover:mr-1',
              task.status === 'done' ? 'bg-green-100 border border-green-500' : ''
            )}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onUpdateTask(task.id, { status: 'done' });
            }}
            aria-label="Mark as done"
            title="Mark as done"
          >
            <Check size={19} className="flex-shrink-0 text-green-500" />
            <span className="ml-1 whitespace-nowrap text-xs font-medium opacity-0 group-hover/mark-done:opacity-100 transition-opacity duration-200 text-green-500">
              Done
            </span>
          </button>
        )}

        {/* More options button */}
        <button
          ref={menuButtonRef}
          className={cn(
            'rounded-full transition-colors p-1.5',
            isMobile
              ? 'opacity-100 text-gray-500 bg-[var(--content-bg-color)]' // Always visible on mobile
              : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-500 hover:bg-gray-100' // Visible on hover for desktop
          )}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleMenuClick}
          aria-label="Task options"
        >
          <MoreVertical size={16} />
        </button>

        {/* Task menu using the portal in the Dropdown component */}
        {isMenuOpen && menuButtonPosition && (
          <DashboardTaskMenu
            task={task}
            position={menuButtonPosition}
            onClose={() => setIsMenuOpen(false)}
            onDelete={onDeleteTask}
            onUpdateTask={onUpdateTask}
            onNavigateToTask={onNavigateToTask}
            onOpenDatePicker={handleOpenDatePicker}
          />
        )}
      </div>

      {/* Date picker for deadline change */}
      {isDatePickerOpen && (
        <div className="date-picker-wrapper">
          {ReactDOM.createPortal(
            <FloatingDatePicker
              selectedDate={task.deadline ? new Date(task.deadline) : null}
              positionSource={
                isMobile
                  ? { type: 'fullscreen' }
                  : { type: 'menuPosition', position: menuButtonPosition }
              }
              onDateChange={date => {
                onUpdateTask(task.id, { deadline: date ? date.toISOString() : null });
              }}
              onClose={() => setIsDatePickerOpen(false)}
              launchedFromMenu={true}
            />,
            document.body
          )}
        </div>
      )}

      {/* Project and topic info - allowing natural wrapping when needed */}
      <div className="mb-1 max-w-[calc(100%-60px)]">
        <span className="text-xs text-gray-400 text-base break-words">
          <span className="font-bold">{projectName}</span> / {topicName}
        </span>
      </div>

      {/* Task title - more prominent with Montserrat font */}
      <div className="flex items-center gap-1.5">
        <h3
          className={cn(
            'text-gray-900 font-semibold text-base mb-1.5 font-montserrat pr-1 flex-1',
            isMobile && 'text-lg'
          )}
        >
          {task.title}
        </h3>
        {(task as any).isHighlighted && sectionType !== 'highlighted' && (
          <Star size={16} className="text-yellow-500 fill-yellow-500 flex-shrink-0 mb-1.5" />
        )}
      </div>

      {/* Description with better spacing */}
      {task.description && (
        <p className="text-sm text-gray-400 mb-3 line-clamp-2">{task.description}</p>
      )}

      {/* Deadline info on its own line with proper styling */}
      {dateInfo && (
        <div
          className={`${dateInfo.color} text-xs ${dateInfo.isPast || dateInfo.isToday ? 'font-semibold' : ''}`}
        >
          <div className="flex items-center gap-1.5">
            <Clock
              size={dateInfo.isPast || dateInfo.isToday ? 14 : 12}
              className={`${dateInfo.color} ${dateInfo.isPast || dateInfo.isToday ? 'stroke-[2.5]' : ''}`}
            />
            <span>{dateInfo.text}</span>
          </div>
        </div>
      )}

      {/* Tags with improved spacing */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {task.tags.map((tag, index) => (
            <span
              key={index}
              className="text-xs bg-[var(--content-bg-color)] text-gray-600 px-2 py-0.5 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Assignee information displayed as tooltip icon in bottom right */}
      {(task as any).assignedTo && (
        <div
          className="absolute bottom-2 right-2 z-10"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <UserInfoTooltip
            userId={(task as any).assignedTo}
            fallbackName={(task as any).assigneeName}
            size="xs"
            position="top"
            iconType="circle"
            bgColor={bgColor}
          />
        </div>
      )}
    </div>
  );
};

// Helper function for class names
const cn = (...classes: string[]) => {
  return classes.filter(Boolean).join(' ');
};

export default DashboardTaskCard;
